import React, {
  useEffect, useLayoutEffect, useRef, useState
} from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  AR, ArrowDown, ArrowUp, Play, SpecialSavings, ThreeSixtySpin
} from '@one-thd/sui-icons';
import { useConfigService } from '@thd-nucleus/experience-context';
import Helper from '../../Helper';
import Image from '../../common/Image';
import FloatingActionButton from '../../common/FloatingActionButton';
import {
  buildSrcSet,
  deriveSizeUrl,
  SIZES_HERO,
  SIZES_TILE,
  getMediaStillUrl,
  isThdCdnImage
} from '../../common/imageUrlUtils';
import { ReactImageMagnify } from '../ReactImageMagnify/ReactImageMagnify';
import { staticImageGridPropTypes, staticImageGridDefaultProps, mediaShape } from './propTypes';
import {
  DEFAULT_VISIBLE_TILE_COUNT,
  GALLERY_LABEL,
  HERO_ASPECT_RATIO,
  HERO_IMAGE_SIZE,
  HERO_INTRINSIC_HEIGHT,
  HERO_INTRINSIC_WIDTH,
  TILE_ASPECT_RATIO,
  TILE_IMAGE_SIZE,
  TILE_INTRINSIC_HEIGHT,
  TILE_INTRINSIC_WIDTH,
  ZOOM_IMAGE_SIZE,
  ZOOM_INTRINSIC_SIZE
} from './constants';

const buildAltText = ({ media, identifiers, index }) => {
  if (media?.altText) { return media.altText; }
  const { brandName = '', productLabel = '' } = identifiers || {};
  const label = `${brandName} ${productLabel}`.trim();
  return label ? `${label} - image ${index + 1}` : `Product image ${index + 1}`;
};

/**
 * Only plain images zoom. Video, 360 and AR paint a still that stands in for the asset
 * rather than being it, and a shoppable image is badged as a surface to open.
 */
const isZoomable = (media) => Helper.isImage({ media }) && !Helper.isHotspot({ media });

/**
 * A 2 percent tint over every still, so a white product background reads as a tile
 * against the white page instead of melting into it.
 */
const TINT_CLASSES = classNames(
  'sui-absolute sui-inset-0 sui-pointer-events-none sui-rounded-xl',
  'sui-bg-inverse sui-bg-opacity-[.02]'
);

/**
 * Video stills are 16:9 frames, so contain would letterbox them inside the tile; they
 * fill it instead. Catalog images are square files and stay contained, uncropped.
 */
const getObjectFitClass = (media) => (Helper.isVideo({ media }) ? 'sui-object-cover' : 'sui-object-contain');

/**
 * The magnifier sizes its own wrapper and image inline, so the fill behaviour the tiles
 * depend on has to travel as a style override rather than a class.
 */
const MAGNIFY_CONTAINER_STYLE = { width: '100%', height: '100%' };
const MAGNIFY_IMAGE_STYLE = { height: '100%' };
const MAGNIFY_ENLARGED_DIMENSIONS = { width: '100%', height: '100%' };
/**
 * The in place zoom panel paints above the tile tint, so it carries the same 2 percent
 * tint on a pseudo element, or a hovered tile would turn back to white.
 */
const MAGNIFY_ENLARGED_CLASSES = classNames(
  'sui-bg-primary sui-z-tooltip sui-rounded-xl',
  'after:sui-content-[""] after:sui-absolute after:sui-inset-0 after:sui-pointer-events-none',
  'after:sui-bg-inverse after:sui-bg-opacity-[.02]'
);

/**
 * Hover zoom (PIP1-328): the gallery's own ReactImageMagnify, configured exactly as the
 * main image is, so a tile behaves on hover the way the image it replaces does.
 *
 * `fs:is-hover-zoom-over-main-image-enabled` is the same switch `MainImage` reads, so the
 * grid follows the main gallery between the in-place zoom and the lens-plus-panel one
 * rather than pinning a mode of its own.
 *
 * The enlarged layer mounts only while hovered, so the 1000px asset costs nothing on load.
 */
const ZoomableStill = ({
  stillUrl,
  alt,
  className,
  imageSize,
  intrinsicWidth,
  intrinsicHeight,
  sizes,
  lazy,
  fetchPriority
}) => {
  const isHoverZoomOverMainImageEnabled = useConfigService('fs:is-hover-zoom-over-main-image-enabled');

  return (
    <ReactImageMagnify
      smallImage={{
        alt,
        src: stillUrl,
        srcSet: buildSrcSet(stillUrl, imageSize),
        sizes,
        isFluidWidth: true,
        className,
        intrinsicWidth,
        intrinsicHeight,
        loading: lazy ? 'lazy' : undefined,
        decoding: 'async',
        style: MAGNIFY_IMAGE_STYLE
      }}
      largeImage={{
        alt,
        src: deriveSizeUrl(stillUrl, imageSize, ZOOM_IMAGE_SIZE),
        width: ZOOM_INTRINSIC_SIZE,
        height: ZOOM_INTRINSIC_SIZE
      }}
      enlargedImagePosition={isHoverZoomOverMainImageEnabled ? 'over' : 'beside'}
      enlargedImageContainerClassName={MAGNIFY_ENLARGED_CLASSES}
      enlargedImageContainerDimensions={MAGNIFY_ENLARGED_DIMENSIONS}
      containerStyle={MAGNIFY_CONTAINER_STYLE}
      fetchPriority={fetchPriority}
    />
  );
};

ZoomableStill.propTypes = {
  stillUrl: PropTypes.string.isRequired,
  alt: PropTypes.string,
  className: PropTypes.string,
  imageSize: PropTypes.string.isRequired,
  intrinsicWidth: PropTypes.number.isRequired,
  intrinsicHeight: PropTypes.number.isRequired,
  sizes: PropTypes.string,
  lazy: PropTypes.bool,
  fetchPriority: PropTypes.oneOf(['high', 'low', 'auto'])
};

ZoomableStill.defaultProps = {
  alt: '',
  className: undefined,
  sizes: undefined,
  lazy: true,
  fetchPriority: 'auto'
};

const getMediaTypeBadge = (media) => {
  if (Helper.isVideo({ media })) { return { Icon: Play, label: 'Video' }; }
  if (Helper.isAR({ media })) { return { Icon: AR, label: 'View in AR' }; }
  if (Helper.isImage360({ media })) { return { Icon: ThreeSixtySpin, label: '360° view' }; }
  if (Helper.isHotspot({ media })) { return { Icon: SpecialSavings, label: 'Shop This Look' }; }
  return null;
};

const MediaTypeBadge = ({ media, size = 'md' }) => {
  const badge = getMediaTypeBadge(media);
  if (!badge) { return null; }

  const { Icon, label } = badge;
  const circleClasses = classNames(
    'sui-absolute sui-inset-0 sui-m-auto sui-pointer-events-none sui-rounded-full',
    'sui-bg-inverse sui-bg-opacity-60 sui-flex sui-items-center sui-justify-center sui-shadow-md',
    { 'sui-h-16 sui-w-16': size === 'lg', 'sui-h-10 sui-w-10': size !== 'lg' }
  );

  return (
    <>
      <span className={circleClasses} data-testid="media-type-badge">
        <Icon
          color="inverse"
          size={size === 'lg' ? 'medium' : 'small'}
          aria-hidden="true"
          style={Icon === Play ? { transform: 'translateX(1px)' } : undefined}
        />
      </span>
      <span className="sui-sr-only">{label}</span>
    </>
  );
};

MediaTypeBadge.propTypes = {
  media: mediaShape,
  size: PropTypes.oneOf(['md', 'lg'])
};

MediaTypeBadge.defaultProps = {
  media: undefined,
  size: 'md'
};

/**
 * Renders the hero or a tile: a zoomable still for plain images, a plain <Image> otherwise.
 * Hero and tile only differ in size/priority props, so this carries both call sites.
 */
const GalleryImage = ({
  media, stillUrl, alt, className, imageSize, intrinsicWidth, intrinsicHeight, sizes, lazy, fetchPriority
}) => {
  const imageClassName = classNames(className, getObjectFitClass(media));

  return isZoomable(media) ? (
    <ZoomableStill
      stillUrl={stillUrl}
      alt={alt}
      className={imageClassName}
      imageSize={imageSize}
      intrinsicWidth={intrinsicWidth}
      intrinsicHeight={intrinsicHeight}
      sizes={sizes}
      lazy={lazy}
      fetchPriority={fetchPriority}
    />
  ) : (
    <Image
      src={stillUrl}
      alt={alt}
      className={imageClassName}
      width={intrinsicWidth}
      height={intrinsicHeight}
      lazy={lazy}
      fetchPriority={fetchPriority}
      decoding="async"
      srcSet={isThdCdnImage(media) ? buildSrcSet(stillUrl, imageSize) : undefined}
      sizes={isThdCdnImage(media) ? sizes : undefined}
    />
  );
};

GalleryImage.propTypes = {
  media: mediaShape,
  stillUrl: PropTypes.string.isRequired,
  alt: PropTypes.string,
  className: PropTypes.string,
  imageSize: PropTypes.string.isRequired,
  intrinsicWidth: PropTypes.number.isRequired,
  intrinsicHeight: PropTypes.number.isRequired,
  sizes: PropTypes.string,
  lazy: PropTypes.bool,
  fetchPriority: PropTypes.oneOf(['high', 'low', 'auto'])
};

GalleryImage.defaultProps = {
  media: undefined,
  alt: '',
  className: undefined,
  sizes: undefined,
  lazy: true,
  fetchPriority: 'auto'
};

/**
 * Static two-column media gallery for the information-focus PIP layout.
 *
 * Layout is pure CSS grid so the markup is fully server-rendered and requires no
 * measurement or hydration work on the client. Column collapse is handled by CSS
 * breakpoints rather than channel detection so the grid reflows as the viewport shrinks.
 */
const StaticImageGrid = ({
  heroMedia = staticImageGridDefaultProps.heroMedia,
  tileMedia = staticImageGridDefaultProps.tileMedia,
  identifiers = staticImageGridDefaultProps.identifiers,
  overrideData = staticImageGridDefaultProps.overrideData,
  hideProductImagesLabel = staticImageGridDefaultProps.hideProductImagesLabel,
  visibleTileCount = DEFAULT_VISIBLE_TILE_COUNT,
  showMoreButton = staticImageGridDefaultProps.showMoreButton,
  onShowMore = staticImageGridDefaultProps.onShowMore,
  onMediaClick = staticImageGridDefaultProps.onMediaClick,
  heroFetchPriority = staticImageGridDefaultProps.heroFetchPriority,
  itemId = staticImageGridDefaultProps.itemId
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleRef = useRef(null);
  const collapseScrollAnchorRef = useRef(null);
  const gridRootRef = useRef(null);

  // Roomvo's script waits for a `.mediagallery` element before it scans for the STIMR buttons,
  // fetches product mappings and reveals them. It is an integration hook, not styling, so it is
  // added at runtime to keep the Stencil class check (sui- only) passing.
  useEffect(() => {
    const root = gridRootRef.current;
    if (!root) return undefined;
    root.classList.add('mediagallery');
    return () => root.classList.remove('mediagallery');
  }, []);

  useLayoutEffect(() => {
    if (isExpanded || collapseScrollAnchorRef.current === null || !toggleRef.current) {
      return;
    }

    const buttonPositionBefore = collapseScrollAnchorRef.current;
    const buttonPositionAfter = toggleRef.current.getBoundingClientRect().top;
    const pixelsButtonMoved = buttonPositionAfter - buttonPositionBefore;

    collapseScrollAnchorRef.current = null;

    if (pixelsButtonMoved !== 0) {
      window.scrollBy({ top: pixelsButtonMoved, left: 0, behavior: 'auto' });
    }
  }, [isExpanded]);

  const [hero] = heroMedia || [];
  const visibleTileLimit = isExpanded ? (tileMedia || []).length : visibleTileCount;
  const tiles = (tileMedia || []).slice(1, visibleTileLimit);
  const heroStill = getMediaStillUrl(hero);

  if (!hero || !heroStill) {
    return null;
  }

  const galleryLabel = overrideData?.galleryLabel || GALLERY_LABEL;
  const hasOverflow = (tileMedia || []).length > visibleTileCount;

  const handleToggleExpand = () => {
    const willExpand = !isExpanded;
    if (!willExpand && toggleRef.current) {
      collapseScrollAnchorRef.current = toggleRef.current.getBoundingClientRect().top;
    }

    setIsExpanded(willExpand);

    if (willExpand && onShowMore) {
      onShowMore();
    }
  };

  /**
   * A tile cannot clip with overflow-hidden, since the beside zoom panel renders outside
   * it, so the wrapper, the still, the trigger, the tint and the zoom panel each carry
   * sui-rounded-xl themselves.
   */
  const tileClasses = classNames(
    'sui-w-full',
    'sui-h-full',
    'sui-bg-primary',
    'sui-rounded-xl'
  );

  const triggerClasses = classNames(
    'sui-absolute sui-inset-0 sui-w-full sui-h-full sui-p-0 sui-m-0',
    'sui-border-0 sui-bg-transparent sui-cursor-pointer sui-rounded-xl'
  );

  return (
    <div
      ref={gridRootRef}
      className="sui-flex sui-flex-col sui-gap-4 sui-w-full"
      data-component="StaticImageGrid"
      data-testid="static-image-grid"
    >
      {!hideProductImagesLabel && (
        <h2 className="sui-sr-only">{galleryLabel}</h2>
      )}

      <div
        className="sui-relative sui-w-full sui-rounded-xl sui-bg-subtle"
        style={{ aspectRatio: HERO_ASPECT_RATIO }}
        data-testid="static-image-grid-hero"
      >
        <button
          type="button"
          className={triggerClasses}
          onClick={onMediaClick ? () => onMediaClick(hero, 0) : undefined}
          data-testid="static-image-grid-hero-trigger"
        >
          <GalleryImage
            media={hero}
            stillUrl={heroStill}
            alt={buildAltText({ media: hero, identifiers, index: 0 })}
            className={tileClasses}
            imageSize={HERO_IMAGE_SIZE}
            intrinsicWidth={HERO_INTRINSIC_WIDTH}
            intrinsicHeight={HERO_INTRINSIC_HEIGHT}
            sizes={SIZES_HERO}
            lazy={false}
            fetchPriority={heroFetchPriority}
          />
          <span className={TINT_CLASSES} aria-hidden="true" data-testid="static-image-grid-tint" />
          <MediaTypeBadge media={hero} size="lg" />
        </button>
        {!!itemId && (
          <div
            className={classNames(
              'sui-absolute sui-bottom-4 sui-left-1/2 -sui-translate-x-1/2 sui-z-10',
              'sui-flex sui-gap-2 sui-pointer-events-none'
            )}
            data-testid="static-image-grid-roomvo-buttons"
          >
            <FloatingActionButton itemId={itemId} variant="pill" buttonClassName="sui-pointer-events-auto" />
            <FloatingActionButton
              itemId={itemId}
              variant="pill"
              virtualSample
              text="View Virtual Sample"
              buttonClassName="sui-pointer-events-auto"
            />
          </div>
        )}
      </div>

      {tiles.length > 0 && (
        <ul
          className="sui-grid sui-grid-cols-1 sm:sui-grid-cols-2 sui-gap-4 sui-list-none sui-p-0 sui-m-0"
          data-testid="static-image-grid-tiles"
        >
          {tiles.map((media, index) => {
            const stillUrl = getMediaStillUrl(media);
            if (!stillUrl) { return null; }

            return (
              <li
                key={media.id || media.url}
                className="sui-relative sui-rounded-xl sui-bg-subtle"
                style={{ aspectRatio: TILE_ASPECT_RATIO }}
              >
                <button
                  type="button"
                  className={triggerClasses}
                  onClick={onMediaClick ? () => onMediaClick(media, index + 1) : undefined}
                  data-testid={`static-image-grid-tile-trigger-${index}`}
                >
                  <GalleryImage
                    media={media}
                    stillUrl={stillUrl}
                    alt={buildAltText({ media, identifiers, index: index + 1 })}
                    className={tileClasses}
                    imageSize={TILE_IMAGE_SIZE}
                    intrinsicWidth={TILE_INTRINSIC_WIDTH}
                    intrinsicHeight={TILE_INTRINSIC_HEIGHT}
                    sizes={SIZES_TILE}
                    lazy
                    fetchPriority="low"
                  />
                  <span className={TINT_CLASSES} aria-hidden="true" data-testid="static-image-grid-tint" />
                  <MediaTypeBadge media={media} size="md" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {showMoreButton && hasOverflow && (
        <div className="sui-flex sui-justify-center sui--mt-8 sui-z-10">
          <button
            type="button"
            ref={toggleRef}
            aria-label={isExpanded ? 'Show fewer product images' : 'Show more product images'}
            aria-expanded={isExpanded}
            data-testid="static-image-grid-show-more"
            onClick={handleToggleExpand}
            className={classNames(
              'sui-flex sui-items-center sui-justify-center',
              'sui-h-12 sui-w-12 sui-rounded-full',
              'sui-bg-primary sui-shadow-lg sui-border-1 sui-border-solid sui-border-primary'
            )}
          >
            {isExpanded ? <ArrowUp size="small" /> : <ArrowDown size="small" />}
          </button>
        </div>
      )}
    </div>
  );
};

StaticImageGrid.displayName = 'StaticImageGrid';

StaticImageGrid.propTypes = staticImageGridPropTypes;

export default StaticImageGrid;
